import { useEffect, useRef, useState } from "react";
import "./AppEmployeeRecord.css";
import "./AppEmployeeRecords.css";
import { 
    AppGenericProps,
    CheckContractor,
    CheckEmployee,
    CheckResponseError,
    CustomProperty,
    CustomPropertyType,
    CustomPropertyValue,
    EmployeeRecord,
    EmployeeType,
    PaidTimeOffAdjustment,
    PaidTimeOffPolicy,
    TimeOffRequest
} from "winslow-core";
import { AppEmployeeRecordTypeSelector } from "./AppEmployeeRecordTypeSelector";
import { AppEmployeeRecordCheckForm } from "./AppEmployeeRecordCheckForm";
import { useNavigate } from "react-router-dom";
import { EmployeeRecordSelect } from "../../shared/EmployeeRecordSelect";
import { DEFAULT_NEW_EMPLOYEE_RECORD, EmployeeTypeDisplayNames, ESSENTIAL_DESCRIPTIONS } from "./employeeRecordConfig";
// import { PropertyDefinitionSelect } from "../../shared/PropertyDefinitionSelect";
// import { DaysOfWork } from "./DaysOfWork";
// import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
// import { RadioButtons } from "./RadioButtons";
// import { SalaryType } from "winslow-core";
import { 
    apiGetCustomPropertiesNotVibeForWorkspace,
    apiGetEmployeeRecord,
} from "../../../services/EmployeeRecordService";
import { useDispatch, useSelector } from "react-redux";
import { selectUIStates, selectUser, setUIStates } from "../../../redux/features/appData";
import toast from "react-hot-toast";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import { toastFail, toastSuccess } from "../../../config";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { validateEmail } from "../../uiHelpers";
import { Calculator, FileText, Wallet, User, AlertTriangle } from "lucide-react";
import ModalConfirmChangeType from "./ModalConfirmChangeType";
import { apiGetPaidTimeOffAdjustment, apiGetPaidTimeOffAdjustmentsForEmployeeRecord } from "../../../services/PaidTimeOffAdjustmentService";
import { apiGetPaidTimeOffPoliciesForWorkspace, apiGetPaidTimeOffPolicy } from "../../../services/PaidTimeOffPolicyService";
import { apiGetTimeOffRequestsForEmployeeRecord } from "../../../services/TimeOffRequestService";
import { Button } from "../../../ds2/components/Button";
import { AppCustomPropertyError } from "../app-customize/AppCustomPropertyError";
import { EmployeeProfileContent } from "../../employee-profile-sidebar/EmployeeProfileContent";
import { AppEmployeeRecordPayrollEmployee } from "./AppEmployeeRecordPayrollEmployee";
import { AppEmployeeRecordPTOAdjustments } from "./AppEmployeeRecordPTOAdjustments";
import { AppEmployeeRecordPTORequests } from "./AppEmployeeRecordPTORequests";
import { AppEmployeeRecordProfile } from "./AppEmployeeRecordProfile";
import { apiCheckGetContractor, apiCheckGetEmployee } from "../../../services/PayrollService";
import { AppCheckErrorMessage } from "../app-payroll/AppCheckErrorMessage";
import { AppEmployeeRecordPayrollContractor } from "./AppEmployeeRecordPayrollContractor";

const datePickerFormat = "MM/dd/yyyy";

type AppEmployeeRecordProps = {
    id: string | undefined;  // undefined === new
    genericProps?: AppGenericProps;
}

export const AppEmployeeRecord = ({id, genericProps} : AppEmployeeRecordProps) => {

    const storeUser = useSelector(selectUser);
    const uiStates = useSelector(selectUIStates);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    
    const [employeeType, setEmployeeType] = useState<EmployeeType | undefined>(undefined);
    const [originalEmployeeRecord, setOriginalEmployeeRecord] = useState<Partial<EmployeeRecord> | undefined>(undefined);
    const [newEmployeeRecord, setNewEmployeeRecord] = useState<Partial<EmployeeRecord>>(DEFAULT_NEW_EMPLOYEE_RECORD);

    // const [propertyDefinitions, setPropertyDefinitions] = useState<PropertyDefinition[] | undefined>(undefined);
    const [customProperties, setCustomProperties] = useState<CustomProperty[] | undefined>(undefined);

    const [validateChangeType, setValidateChangeType] = useState<boolean>(false);
    const [changeTypeTo, setChangeTypeTo] = useState<EmployeeType | undefined>(undefined);
    
    const [paidTimeOffAdjustments, setPaidTimeOffAdjustments] = useState<PaidTimeOffAdjustment[] | undefined>(undefined);
    const [paidTimeOffPolicies, setPaidTimeOffPolicies] = useState<PaidTimeOffPolicy[] | undefined>(undefined);
    const [timeOffRequests, setTimeOffRequests] = useState<TimeOffRequest[] | undefined>(undefined);
    
    const [checkEmployee, setCheckEmployee] = useState<CheckEmployee | undefined>(undefined);
    const [checkContractor, setCheckContractor] = useState<CheckContractor | undefined>(undefined);

    const [checkResponseError, setCheckResponseError] = useState<CheckResponseError | undefined>(undefined);

    const PTO_TABS = {
        PROFILE: "profile",
        ADJUSTMENTS: "pto-adjustments",
        REQUESTS: "time-off-requests",
        PAYROLL: "payroll",
    };
    const [ptoTab, setPtoTab] = useState<string | null>(PTO_TABS.PROFILE);

    const [resetKey, setResetKey] = useState<number>(0);

    const containerRef = useRef(null);

    useEffect(() => {
        if (id) {
            // Load existing employee record
            (async () => {
                await loadEmployeeData(id);
            })();
        } else {
            dispatch(setUIStates({...uiStates, hrisLocation: "Add Employee"}));
            setNewEmployeeRecord(DEFAULT_NEW_EMPLOYEE_RECORD);
            setOriginalEmployeeRecord(undefined);
        }
    }, [id]);

    useEffect(() => {
        if (!storeUser?.currentWorkspaceId) {
            return;
        }

        if (!customProperties && employeeType) {
            (async () => {
                const cp = await apiGetCustomPropertiesNotVibeForWorkspace(storeUser?.currentWorkspaceId || "");

                if (!cp) {
                    toast("Error getting custom properties");
                    return;
                }

                const cpByType = cp.filter((customProperty: CustomProperty) => {
                    return !customProperty.isVibe && customProperty.visibleEmployeeTypes.includes(employeeType)
                });

                setCustomProperties(cpByType);
            })();
        }
    }, [storeUser, employeeType]);

    useEffect(() => {
        console.log("[AppEmployeeRecord/ue] new employee record: ", newEmployeeRecord);
    }, [newEmployeeRecord]);

    useEffect(() => {
        // Reset tab to PROFILE if current tab is PTO-related and employee type doesn't support it
        if (employeeType && ptoTab && (ptoTab === PTO_TABS.ADJUSTMENTS || ptoTab === PTO_TABS.REQUESTS)) {
            const isUSFullOrPartTime = employeeType === EmployeeType.US_FULL_TIME || employeeType === EmployeeType.US_PART_TIME;
            if (!isUSFullOrPartTime) {
                setPtoTab(PTO_TABS.PROFILE);
            }
        }
    }, [employeeType, ptoTab]);

    const loadEmployeeData = async (id: string) => {    

        if (!id) {
            toast("Error getting employee record: No ID", toastFail);
            return;
        }

        dispatch(setUIStates({...uiStates, hrisLocation: "Edit Employee"}));

        const er = await apiGetEmployeeRecord(id);
        
        if (!er) {
            toast("Error getting employee record", toastFail);
            console.error("Error getting employee record: ", id);
            return;
        }

        // save this in case its an update
        console.log("[AppEmployeeRecord] loading employee data: ", er);
        setOriginalEmployeeRecord({...er});
        setNewEmployeeRecord({...er});   
        setEmployeeType(er.employeeType);

        // get these all non blocking
        apiGetPaidTimeOffAdjustmentsForEmployeeRecord(er.id).then((adjustments) => {
            if (!adjustments) {
                toast("Error getting PTO adjustments", toastFail);
                return;
            }

            setPaidTimeOffAdjustments(adjustments);
        });

        apiGetPaidTimeOffPoliciesForWorkspace(storeUser?.currentWorkspaceId || "").then((policies) => {
            if (!policies) {
                toast("Error getting PTO policies", toastFail);
                return;
            }

            setPaidTimeOffPolicies(policies);
        });

        apiGetTimeOffRequestsForEmployeeRecord(er.id).then((requests) => {
            if (!requests) {
                toast("Error getting time off requests", toastFail);
                return;
            }

            setTimeOffRequests(requests);
        });

        loadCheckEmployee(er.checkEmployeeId || "");   
        loadCheckContractor(er.checkContractorId || "");
    }

    const loadCheckEmployee = async (checkEmployeeId: string) => {
        if (!checkEmployeeId) {
            return;
        }

        const apiResponse = await apiCheckGetEmployee(checkEmployeeId);
        const checkEmployee = apiResponse?.data;
        const checkResponseError = apiResponse?.checkResponseError;

        if (checkResponseError) {
            console.error("[AppEmployeeRecord] checkResponseError: ", checkResponseError);
            return;
        }

        setCheckEmployee(checkEmployee || undefined);
    }

    const loadCheckContractor = async (checkContractorId: string) => {
        if (!checkContractorId) {
            return;
        }

        const apiResponse = await apiCheckGetContractor(checkContractorId);
        const checkContractor = apiResponse?.data;
        const checkResponseError = apiResponse?.checkResponseError;

        if (checkResponseError) {
            console.error("[AppEmployeeRecord] checkResponseError: ", checkResponseError);
            return;
        }

        setCheckContractor(checkContractor || undefined);
    }

    // if someoe change the employee type, reset the custom properties that do not apply
    // to the employee type to null, blank, zero as it may confuse the system to have 
    // data in the DB

    const resetEmployeeRecordCustomProperties = (employeeType: EmployeeType) => {
        const resetEmployeeRecordFields : any = { };

        customProperties?.forEach((customProperty: CustomProperty) => {
            if (!customProperty.visibleEmployeeTypes.includes(employeeType)) {
                const data : any = "";

                switch (customProperty.type) {
                    case CustomPropertyType.SHORT:
                    case CustomPropertyType.LONG:
                    case CustomPropertyType.RICH:
                    case CustomPropertyType.PHONE:
                    case CustomPropertyType.EMAIL:
                    case CustomPropertyType.DATE:
                    case CustomPropertyType.CHECKBOX_SINGLE:
                    case CustomPropertyType.DROPDOWN:
                    case CustomPropertyType.RADIO:
                    case CustomPropertyType.FILE:
                        resetEmployeeRecordFields[customProperty.label] = "";  
                        break;
                    case CustomPropertyType.NUMBER:
                        resetEmployeeRecordFields[customProperty.label] = 0;  
                        break;
                    case CustomPropertyType.CHECKBOX_MULTIPLE:
                        resetEmployeeRecordFields[customProperty.label] = []  
                        break;
                    case CustomPropertyType.EMPLOYEE_RECORD:
                    case CustomPropertyType.PTO_POLICIES:
                        resetEmployeeRecordFields[customProperty.label] = undefined;  
                        break;
                    default:
                        resetEmployeeRecordFields[customProperty.label] = data;
                        break;
                }
            }
        });

        console.log("[AppEmployeeRecord] changing type to: ", employeeType);
        console.log("[AppEmployeeRecord] reset employee record fields: ", resetEmployeeRecordFields);

        setNewEmployeeRecord({
            ...newEmployeeRecord,
            employeeType,
            ...resetEmployeeRecordFields,
        });
    };

    const handleEmployeeTypeChange = (employeeType: EmployeeType) => {
        if (id && id !== "new") {
            setValidateChangeType(true);
            setChangeTypeTo(employeeType);
            return;
        }
        setEmployeeType(employeeType);
        setNewEmployeeRecord({
            ...newEmployeeRecord,
            employeeType: employeeType,
        });
    }

    const handleEmployeeRecordChange = (employeeRecord: Partial<EmployeeRecord>) => {
        console.log("[AppEmployeeRecord] handleEmployeeRecordChange: ", employeeRecord);
        // Only update if we're not currently loading data
        // Check if this is the default record being passed back (which would reset state)
        const isDefaultRecord = !employeeRecord.id && 
                                employeeRecord.workEmail === "" && 
                                employeeRecord.customProperties === "[]";
        
        // Don't update if we're loading an existing record and this looks like a default/reset
        if (id && isDefaultRecord && !originalEmployeeRecord) {
            console.log("[AppEmployeeRecord] Ignoring onChange during initial load - appears to be default record");
            return;
        }
        
        setNewEmployeeRecord({...employeeRecord});
    };

    const handleEmployeeRecordSave = (employeeRecord: Partial<EmployeeRecord>) => {
        setNewEmployeeRecord(employeeRecord);
        setOriginalEmployeeRecord({...employeeRecord});
        navigate("/app/employee-records/" + employeeRecord.id);
    };

    const handleEmployeeRecordUpdate = (updatedRecord: Partial<EmployeeRecord>) => {
        setNewEmployeeRecord(updatedRecord);
        setOriginalEmployeeRecord({...updatedRecord});
    };

    const handleCheckEmployeeCreated = (checkEmployee: CheckEmployee) => {
        setCheckEmployee(checkEmployee);
        setNewEmployeeRecord({...newEmployeeRecord, checkEmployeeId: checkEmployee.id});
        setOriginalEmployeeRecord({...originalEmployeeRecord, checkEmployeeId: checkEmployee.id});
    };

    const handleCheckEmployeeUpdated = async () => {
        await loadCheckEmployee(newEmployeeRecord.checkEmployeeId || "");
    };

    const handleCheckContractorCreated = (checkContractor: CheckContractor) => {
        setCheckContractor(checkContractor);
        setNewEmployeeRecord({...newEmployeeRecord, checkContractorId: checkContractor.id});
        setOriginalEmployeeRecord({...originalEmployeeRecord, checkContractorId: checkContractor.id});
    };

    const handleCheckContractorUpdated = async () => {
        await loadCheckContractor(newEmployeeRecord.checkContractorId || "");
    };

    const isContractorOrVendor = employeeType === EmployeeType.US_CONTRACTOR || employeeType === EmployeeType.US_VENDOR || employeeType === EmployeeType.INT_CONTRACTOR;

    // Determine if Payroll tab should show warning icon
    const shouldShowPayrollWarning = (): boolean => {
        if (!id || !employeeType) return false;
        
        if (isContractorOrVendor) {
            // For contractors/vendors: warning if no checkContractorId OR onboard status != "completed"
            return !newEmployeeRecord.checkContractorId || checkContractor?.onboard?.status !== "completed";
        } else {
            // For employees: warning if no checkEmployeeId OR onboard status != "completed"
            return !newEmployeeRecord.checkEmployeeId || checkEmployee?.onboard?.status !== "completed";
        }
    };

    const showPayrollWarning = shouldShowPayrollWarning();

    console.log("[AppEmployeeRecord] id: ", id);
    console.log("[AppEmployeeRecord] newEmployeeRecord: ", newEmployeeRecord);
    console.log("[AppEmployeeRecord] originalEmployeeRecord: ", originalEmployeeRecord);

    return (
        <div 
            className="app-employee-record-new"
            ref={containerRef}
        >
            {!employeeType &&
                <AppEmployeeRecordTypeSelector 
                    onSubmit={(employeeType: EmployeeType) => {
                        setEmployeeType(employeeType);
                        setNewEmployeeRecord({
                            ...newEmployeeRecord,
                            employeeType,
                        });
                        
                    }}
                />
            }

            {employeeType && id &&
                <div className="app-employee-records-tabs" style={{ marginBottom: '16px' }}>
                    <div 
                        className={ptoTab === PTO_TABS.PROFILE ? 
                            "app-employee-records-tab-selected" : "app-employee-records-tab"
                        }
                        onClick={() => setPtoTab(PTO_TABS.PROFILE)}
                    >
                        <User size={16} />
                        <span>Profile</span>
                    </div>
                    <div 
                        className={ptoTab === PTO_TABS.PAYROLL ? 
                            "app-employee-records-tab-selected" : "app-employee-records-tab"
                        }
                        onClick={() => setPtoTab(PTO_TABS.PAYROLL)}
                    >
                        <Wallet size={16} />
                        <span>Payroll</span>
                        {showPayrollWarning && (
                            <AlertTriangle 
                                size={16} 
                                style={{ marginLeft: "4px", color: "#f59e0b" }} 
                            />
                        )}
                    </div>
                    {(employeeType === EmployeeType.US_FULL_TIME || employeeType === EmployeeType.INT_FULL_TIME || employeeType === EmployeeType.US_PART_TIME) && (
                        <>
                            <div 
                                className={ptoTab === PTO_TABS.ADJUSTMENTS ? 
                                    "app-employee-records-tab-selected" : "app-employee-records-tab"
                                }
                                onClick={() => setPtoTab(PTO_TABS.ADJUSTMENTS)}
                            >
                                <Calculator size={16} />
                                <span>PTO Adjustments</span>
                            </div>
                            <div 
                                className={ptoTab === PTO_TABS.REQUESTS ? 
                                    "app-employee-records-tab-selected" : "app-employee-records-tab"
                                }
                                onClick={() => setPtoTab(PTO_TABS.REQUESTS)}
                            >
                                <FileText size={16} />
                                <span>Time Off Requests</span>
                            </div>
                        </>
                    )}
                </div>
            }

            <AppCheckErrorMessage checkResponseError={checkResponseError} />

            {/* Show Profile tab content when Profile tab is selected or when creating a new record */}
            {(ptoTab === PTO_TABS.PROFILE || !id) && employeeType && newEmployeeRecord && customProperties &&
                <AppEmployeeRecordProfile
                    key={"employee-record-" + (originalEmployeeRecord?.id || "new")}
                    id={id}
                    employeeType={employeeType}
                    newEmployeeRecord={newEmployeeRecord}
                    originalEmployeeRecord={originalEmployeeRecord}
                    customProperties={customProperties}
                    resetKey={resetKey}
                    onEmployeeTypeChange={handleEmployeeTypeChange}
                    onResetKeyChange={() => {
                        setResetKey(resetKey + 1);
                    }}
                    onChange={handleEmployeeRecordChange}
                    onSave={handleEmployeeRecordSave}
                    onUpdate={handleEmployeeRecordUpdate}
                />
            }


            {ptoTab === PTO_TABS.ADJUSTMENTS && employeeType && id && 
                <AppEmployeeRecordPTOAdjustments
                    paidTimeOffAdjustments={paidTimeOffAdjustments}
                    paidTimeOffPolicies={paidTimeOffPolicies}
                />
            }

            {ptoTab === PTO_TABS.REQUESTS && employeeType && id &&
                <AppEmployeeRecordPTORequests
                    timeOffRequests={timeOffRequests}
                    paidTimeOffPolicies={paidTimeOffPolicies}
                />
            }

            {ptoTab === PTO_TABS.PAYROLL && employeeType && id &&
                <>
                    {isContractorOrVendor &&
                        <AppEmployeeRecordPayrollContractor
                            employeeRecord={newEmployeeRecord as EmployeeRecord}
                            checkContractor={checkContractor || null}
                            onCheckContractorCreated={handleCheckContractorCreated}
                            onCheckContractorUpdated={handleCheckContractorUpdated}
                        />
                    }
                    {!isContractorOrVendor && 
                        <AppEmployeeRecordPayrollEmployee
                            employeeRecord={newEmployeeRecord as EmployeeRecord}
                            checkEmployee={checkEmployee || null}
                            onCheckEmployeeCreated={handleCheckEmployeeCreated}
                            onCheckEmployeeUpdated={handleCheckEmployeeUpdated}
                        />
                    }
                </>
            }

            {validateChangeType && changeTypeTo &&
                <ModalConfirmChangeType
                    isOpen={validateChangeType}
                    onClose={() => {
                        setValidateChangeType(false)
                        setChangeTypeTo(undefined);
                    }}
                    onContinue={() => {
                        setEmployeeType(changeTypeTo);
                        resetEmployeeRecordCustomProperties(changeTypeTo);
                        setValidateChangeType(false);
                        setChangeTypeTo(undefined);
                    }}
                />
            }
        </div>
    )
}


